import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import client from "@/api/client";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { useBranch } from "@/context/BranchContext";
import {
  LayoutDashboard, ShoppingCart, Package, Boxes, Truck, Users2,
  ClipboardList, BarChart3, Store, ScrollText, Receipt, Wallet,
  LogOut, Menu, Building2, ChevronDown, ClipboardCheck, ArrowLeftRight, Contact, UtensilsCrossed, ShieldCheck, AlertTriangle, PackagePlus, Tag, Tags, HandHeart, Banknote, Wallet2,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin", "branch_head", "financial"] },
  { to: "/pos", label: "Kasir (POS)", icon: ShoppingCart, roles: ["super_admin", "admin", "branch_head", "cashier"] },
  { to: "/laporan-kasir", label: "Laporan Kasir", icon: BarChart3, roles: ["cashier"] },
  { to: "/pesanan", label: "Pesanan Siap Saji", icon: UtensilsCrossed, roles: ["reseller", "super_admin", "admin", "branch_head", "cashier"] },
  { to: "/penjualan", label: "Penjualan", icon: Receipt, roles: ["super_admin", "admin", "branch_head", "cashier"] },
  { to: "/penjualan-khusus", label: "Penjualan Khusus", icon: HandHeart, roles: ["super_admin", "admin"] },
  { to: "/shift", label: "Shift & Kas", icon: Wallet, roles: ["super_admin", "admin", "branch_head", "cashier"] },
  { to: "/setoran", label: "Setoran Kas", icon: Banknote, roles: ["super_admin", "admin", "branch_head"] },
  { to: "/piutang", label: "Piutang Pelanggan", icon: Wallet2, roles: ["super_admin", "admin"] },
  { to: "/produk", label: "Produk", icon: Package, roles: ["super_admin", "admin", "branch_head"] },
  { to: "/kategori", label: "Kategori", icon: Tag, roles: ["super_admin", "admin", "branch_head"] },
  { to: "/harga-region", label: "Harga Jual Region", icon: Tags, roles: ["super_admin", "admin"] },
  { to: "/stok", label: "Stok", icon: Boxes, roles: ["super_admin", "admin", "branch_head", "warehouse"] },
  { to: "/opname", label: "Opname Massal", icon: ClipboardCheck, roles: ["super_admin", "admin", "branch_head", "warehouse"] },
  { to: "/stok-awal", label: "Stok Awal Cepat", icon: PackagePlus, roles: ["super_admin", "admin", "branch_head", "warehouse"] },
  { to: "/pembelian", label: "Pembelian", icon: Truck, roles: ["super_admin", "admin", "branch_head", "warehouse"] },
  { to: "/po", label: "Purchase Order", icon: ClipboardList, roles: ["super_admin", "admin", "branch_head", "warehouse"] },
  { to: "/transfer", label: "Transfer Cabang", icon: ArrowLeftRight, roles: ["super_admin", "admin", "branch_head", "warehouse"] },
  { to: "/supplier", label: "Supplier", icon: Building2, roles: ["super_admin", "admin", "branch_head", "warehouse"] },
  { to: "/pelanggan", label: "Pelanggan", icon: Contact, roles: ["super_admin", "admin", "branch_head"] },
  { to: "/laporan", label: "Laporan", icon: BarChart3, roles: ["super_admin", "admin", "branch_head", "financial"] },
  { to: "/pengguna", label: "Pengguna", icon: Users2, roles: ["super_admin", "admin", "branch_head"] },
  { to: "/hak-akses", label: "Hak Akses Menu", icon: ShieldCheck, roles: ["super_admin"] },
  { to: "/reset", label: "Reset Data", icon: AlertTriangle, roles: ["super_admin"] },
  { to: "/cabang", label: "Cabang", icon: Store, roles: ["super_admin", "admin"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["super_admin", "admin", "branch_head"] },
];

export { NAV };

export default function AppLayout({ children }) {
  const { user, logout, isAllBranch } = useAuth();
  const { isMenuAllowed } = usePermissions();
  const { branches, branchId, selectBranch } = useBranch();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user.role) && isMenuAllowed(user.role, n.to));
  const posMode = location.pathname === "/pos";

  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const isWarehouse = user.role === "warehouse";
  const [newPoCount, setNewPoCount] = useState(0);
  const [pendingDepositCount, setPendingDepositCount] = useState(0);
  useEffect(() => {
    if (!isAdmin && !isWarehouse) return;
    const fetchCount = () => {
      client.get("/purchase-orders", { params: { po_type: "internal" } })
        .then((r) => setNewPoCount(r.data.filter((p) => p.status === "open").length))
        .catch(() => {});
      if (isAdmin) client.get("/cash-deposits", { params: { status: "pending" } })
        .then((r) => setPendingDepositCount(r.data.length))
        .catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 20000);
    return () => clearInterval(t);
  }, [isAdmin, isWarehouse, location.pathname]);

  const Sidebar = (
    <aside className="flex h-full w-64 flex-col bg-slate-900 text-slate-100">
      <div className="flex items-center gap-2 px-5 py-5 border-b border-slate-800">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary font-heading text-lg font-extrabold text-white">K</div>
        <div>
          <div className="font-heading text-lg font-extrabold leading-none tracking-tight">TOKO KU</div>
          <div className="text-[11px] text-slate-400">Manajemen Multi-Cabang</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200",
                  isActive
                    ? "bg-primary text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                )
              }
            >
              <Icon className="h-[18px] w-[18px]" />
              {n.label}
              {n.to === "/po" && newPoCount > 0 && (
                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white" data-testid="po-badge">{newPoCount}</span>
              )}
              {n.to === "/setoran" && pendingDepositCount > 0 && (
                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white" data-testid="deposit-badge">{pendingDepositCount}</span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-slate-800 p-3 text-xs text-slate-400">
        Online-only · PWA · IDR
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className={posMode ? "hidden" : "hidden md:block"}>{Sidebar}</div>

      {open && (
        <div className={cn("fixed inset-0 z-40", posMode ? "" : "md:hidden")}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between gap-3 border-b bg-white px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className={posMode ? "" : "md:hidden"} onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
            {isAllBranch ? (
              <Select value={branchId || "all"} onValueChange={(v) => selectBranch(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[200px]" data-testid="branch-selector">
                  <Building2 className="mr-2 h-4 w-4 text-primary" />
                  <SelectValue placeholder="Pilih cabang" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Cabang</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`branch-opt-${b.code}`}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2 rounded-md bg-slate-100 px-3 py-1.5 text-sm font-medium">
                <Building2 className="h-4 w-4 text-primary" />
                {branches.find((b) => b.id === user.branch_id)?.name || "Cabang"}
              </div>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                  {user.name?.[0]?.toUpperCase()}
                </div>
                <div className="hidden text-left sm:block">
                  <div className="text-sm font-semibold leading-none">{user.name}</div>
                  <div className="text-[11px] text-muted-foreground">{ROLE_LABELS[user.role]}</div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
